import { EventContextProvider, useAnalytics } from "@codat/analytics";
import {
  ClipboardCopy,
  PencilIcon,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import { useQueryClient } from "@tanstack/react-query";
import { Company } from "api/schema/companies/companySchema";
import classNames from "classnames";
import { useCompanies } from "contexts/CompaniesContext";
import { useGetCompany } from "hooks/query/useGetCompany";
import React, { useEffect, useState } from "react";
import { Subject } from "rxjs";

import Button from "components/Button";
import { EditCompanyModalState } from "pages/Companies/Companies";
import EditCompanyModal, {
  EditCompanyFormState,
} from "pages/Companies/EditCompanyModal";

import styles from "./CompanyHeader.module.scss";
import ExportDataButton from "./components/ExportDataButton";
import RefreshData from "./components/RefreshData/RefreshData";

interface CompanyHeaderProps {
  className?: string;
  companyId: string;
  accountingDataConnection?: string;
  companyData: Company | undefined;
  loading: boolean;
  queueSync$: Subject<void>;
}

export const CompanyHeader: React.FC<CompanyHeaderProps> = ({
  className,
  companyId,
  companyData,
  loading,
  queueSync$,
}) => {
  const { sendEvent } = useAnalytics();
  const { addToast } = useToast();
  const { setActiveCompany } = useCompanies();
  const queryClient = useQueryClient();

  const { data, isFetching } = useGetCompany({ companyId });

  useEffect(() => {
    if (isFetching) {
      setActiveCompany(null);
      return;
    }
    if (data) {
      setActiveCompany(data);
    }
  }, [data, setActiveCompany, isFetching]);

  const [showEditCompanyButton, setShowEditCompanyButton] = useState(false);

  const [editCompanyModal, setEditCompanyModal] =
    useState<EditCompanyModalState>({ open: false });

  const openEditCompanyModal = (company: EditCompanyFormState) =>
    setEditCompanyModal({ open: true, company });

  const closeEditCompanyModal = () => setEditCompanyModal({ open: false });

  const handleCompanyEditSuccess = () => {
    closeEditCompanyModal();
    queryClient.invalidateQueries({ queryKey: ["getCompany", { companyId }] });
  };

  const onCopy = () => {
    sendEvent("Copy Id Button - Click");
    addToast({ message: "Copied company ID to clipboard", variant: "success" });
  };

  return (
    <section data-testid="company-header-wrapper" className={className}>
      <div className={styles.companyId}>
        <Typography
          as="div"
          variant="body_sm"
          fontVariant="code"
          isSkeleton={loading}
          skeletonWidth="16.5rem"
          className={classNames(styles.companyId, {
            [styles.skeleton]: loading,
          })}
          data-testid="company-id"
          noMargin
        >
          {companyId}
          <ClipboardCopy
            label="Copy to clipboard"
            data-testid="copy-id-button"
            noBackground
            onCopy={onCopy}
            text={companyId}
            color="interface"
            className={styles.copyButton}
          />
        </Typography>
      </div>
      <div className={styles.containerWrapper}>
        <div
          className={styles.companyInfoContainer}
          onMouseEnter={() => setShowEditCompanyButton(true)}
          onMouseLeave={() => setShowEditCompanyButton(!showEditCompanyButton)}
          data-testid="company-info-container"
        >
          <div className={styles.companyName}>
            <Typography
              variant="heading_xxl"
              as="h1"
              data-testid="company-header" // Selector used by e2e tests
              isSkeleton={loading}
              skeletonHeight="2.25rem"
              skeletonWidth="20rem"
              noMargin
            >
              {companyData?.name}
            </Typography>
            <Button
              label="Edit company name"
              data-testid="company-header-edit-button"
              buttonName="Edit Company Name"
              eventProperties={{ companyId: companyData?.name }}
              className={classNames(styles.editButton, {
                [styles.hover]: !showEditCompanyButton,
              })}
              onClick={() => {
                if (!companyData) return;
                const { id, name, description, tags, products } = companyData;
                openEditCompanyModal({
                  id,
                  name,
                  description,
                  tags,
                  products,
                });
              }}
              onFocus={() => setShowEditCompanyButton(true)}
              onBlur={() => setShowEditCompanyButton(false)}
              variant="tertiary"
            >
              <PencilIcon />
            </Button>
          </div>
          {(!!companyData?.description || loading) && (
            <Typography
              variant="body_md"
              as="h2"
              data-testid="company-description"
              isSkeleton={loading}
              skeletonWidth="20rem"
              className={classNames(styles.companyDescription, {
                [styles.skeleton]: loading,
              })}
              noMargin
            >
              {companyData?.description}
            </Typography>
          )}
        </div>
        <div className={styles.containerButtons}>
          <ExportDataButton
            companyId={companyId}
            companyName={companyData?.name || ""}
            loading={loading}
          />
          <RefreshData
            companyId={companyId}
            pollRate={3000}
            loading={loading}
            companyName={companyData?.name}
            onRefreshClick={() => {
              window.location.reload();
              return true;
            }}
            queueSync$={queueSync$}
          />
          {editCompanyModal.company && (
            <EventContextProvider name="Edit Company Name Modal">
              <EditCompanyModal
                company={editCompanyModal.company}
                onClose={closeEditCompanyModal}
                onSuccess={handleCompanyEditSuccess}
              />
            </EventContextProvider>
          )}
        </div>
      </div>
    </section>
  );
};
